import React from 'react';
import { useAd } from '../../contexts/AdContext';
import { TrendingUp, Eye, MousePointer, DollarSign, BarChart3 } from 'lucide-react';

interface AdMetrics {
  impressions: number;
  clicks: number;
  revenue: number;
  ctr: number;
}

export const AdAnalytics: React.FC = () => {
  const { adConfig, adMetrics } = useAd();

  const calculateTotalMetrics = (): AdMetrics => {
    const metricsArray = Object.values(adMetrics) as AdMetrics[];
    const total = metricsArray.reduce(
      (acc: AdMetrics, metric: AdMetrics) => ({
        impressions: acc.impressions + metric.impressions,
        clicks: acc.clicks + metric.clicks,
        revenue: acc.revenue + metric.revenue,
        ctr: 0
      }),
      { impressions: 0, clicks: 0, revenue: 0, ctr: 0 } as AdMetrics
    );

    total.ctr = total.impressions > 0 ? (total.clicks / total.impressions) * 100 : 0;
    return total;
  };

  const totalMetrics = calculateTotalMetrics();

  return (
    <div className="space-y-6">
      {/* Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center space-x-3">
            <Eye className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <div>
              <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Impressions</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {totalMetrics.impressions.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-center space-x-3">
            <MousePointer className="w-6 h-6 text-green-600 dark:text-green-400" />
            <div>
              <p className="text-sm font-medium text-green-600 dark:text-green-400">Clicks</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {totalMetrics.clicks.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
          <div className="flex items-center space-x-3">
            <TrendingUp className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            <div>
              <p className="text-sm font-medium text-purple-600 dark:text-purple-400">CTR</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {totalMetrics.ctr.toFixed(2)}%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center space-x-3">
            <DollarSign className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            <div>
              <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Revenue</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ${totalMetrics.revenue.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ad Status */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-4">
            <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              Ad Status & Configuration
            </h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Ad Status</span>
              <span className={`text-xs px-2 py-1 rounded-full ${
                adConfig.enabled 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              }`}>
                {adConfig.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Frequency</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                {adConfig.frequency}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Test Mode</span>
              <span className={`text-xs px-2 py-1 rounded-full ${
                adConfig.testMode 
                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
              }`}>
                {adConfig.testMode ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        {/* Performance Insights */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-4">
            <TrendingUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              Performance Insights
            </h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Avg. CTR</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {totalMetrics.ctr.toFixed(2)}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Revenue per Click</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                ${totalMetrics.clicks > 0 ? (totalMetrics.revenue / totalMetrics.clicks).toFixed(2) : '0.00'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Sessions</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {Object.keys(adMetrics).length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Zone-specific Metrics */}
      {Object.keys(adMetrics).length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
            Zone-specific Metrics
          </h3>
          <div className="space-y-3">
            {(Object.entries(adMetrics) as [string, AdMetrics][]).map(([zoneId, metrics]) => (
              <div key={zoneId} className="bg-white dark:bg-gray-600 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Zone: {zoneId}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    CTR: {metrics.ctr.toFixed(2)}%
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Impressions:</span>
                    <span className="ml-1 font-medium text-gray-900 dark:text-white">
                      {metrics.impressions.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Clicks:</span>
                    <span className="ml-1 font-medium text-gray-900 dark:text-white">
                      {metrics.clicks.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Revenue:</span>
                    <span className="ml-1 font-medium text-gray-900 dark:text-white">
                      ${metrics.revenue.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Test Mode Warning */}
      {adConfig.testMode && (
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Test mode is enabled. Real ads will be shown in production.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}; 